import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, ShieldCheck, CreditCard, Wallet } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { fmtINR, fmtUSD } from "@/lib/money";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const FREE_SHIPPING = 2000;
const FLAT_SHIPPING = 99;

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

export default function Checkout() {
  const { items, subtotal, clear } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [method, setMethod] = useState("razorpay");
  const [razorpayCfg, setRazorpayCfg] = useState({ enabled: false });
  const [addr, setAddr] = useState({
    name: user?.name || "",
    line1: "", city: "", state: "", postal_code: "", country: "India", phone: "",
  });
  const submittedRef = useRef(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/payments/razorpay/config");
        setRazorpayCfg(data);
        if (!data.enabled) setMethod("stripe");
      } catch {}
    })();
  }, []);

  const shipping = subtotal >= FREE_SHIPPING ? 0 : FLAT_SHIPPING;
  const tax = +(subtotal * 0.05).toFixed(2);
  const total = +(subtotal + shipping + tax).toFixed(2);

  useEffect(() => {
    if (!user) navigate("/login?next=/checkout");
    else if (items.length === 0) navigate("/cart");
  }, [user, items.length, navigate]);
  if (!user || items.length === 0) return null;

  function validAddr() {
    if (!addr.name || !addr.line1 || !addr.city || !addr.postal_code || !addr.country) {
      toast.error("Please fill all shipping fields");
      return false;
    }
    return true;
  }

  async function payStripe() {
    if (!validAddr()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        shipping_address: addr,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  }

  async function payRazorpay() {
    if (!validAddr()) return;
    if (!razorpayCfg.enabled) {
      toast.error("Razorpay isn't configured yet. Add keys in backend .env to enable.");
      return;
    }
    setLoading(true);
    const ok = await loadRazorpayScript();
    if (!ok) { toast.error("Failed to load Razorpay"); setLoading(false); return; }

    try {
      const { data } = await api.post("/payments/razorpay/order", {
        items: items.map((it) => ({ product_id: it.product_id, quantity: it.quantity })),
        shipping_address: addr,
        origin_url: window.location.origin,
      });

      const options = {
        key: data.key_id,
        amount: data.amount,
        currency: data.currency,
        name: "Mona's Creations",
        description: "Handcrafted Indian treasures",
        order_id: data.razorpay_order_id,
        prefill: { name: data.name, email: data.email, contact: addr.phone },
        theme: { color: "#E65C00" },
        handler: async (r) => {
          if (submittedRef.current) return;
          submittedRef.current = true;
          try {
            await api.post("/payments/razorpay/verify", {
              order_id: data.order_id,
              razorpay_order_id: r.razorpay_order_id,
              razorpay_payment_id: r.razorpay_payment_id,
              razorpay_signature: r.razorpay_signature,
            });
            clear();
            navigate(`/payment/success?order_id=${data.order_id}&provider=razorpay`);
          } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail));
            setLoading(false);
          }
        },
        modal: { ondismiss: () => setLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.on("payment.failed", () => {
        toast.error("Payment failed. Please try again.");
        setLoading(false);
      });
      rzp.open();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setLoading(false);
    }
  }

  function pay() {
    if (method === "razorpay") return payRazorpay();
    return payStripe();
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <h1 className="font-display text-5xl text-[#2C1810] mb-10">Checkout</h1>
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_380px] gap-10">
        <div className="space-y-6">
          <div className="bg-white/90 border border-[#FFD1A6] rounded-3xl p-8 shadow-warm">
            <div className="font-display text-2xl text-[#2C1810] mb-6">Shipping address</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label>Full name</Label>
                <Input data-testid="checkout-name" value={addr.name} onChange={(e) => setAddr({ ...addr, name: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
              <div className="md:col-span-2">
                <Label>Address line</Label>
                <Input data-testid="checkout-line1" value={addr.line1} onChange={(e) => setAddr({ ...addr, line1: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
              <div>
                <Label>City</Label>
                <Input data-testid="checkout-city" value={addr.city} onChange={(e) => setAddr({ ...addr, city: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
              <div>
                <Label>State</Label>
                <Input data-testid="checkout-state" value={addr.state} onChange={(e) => setAddr({ ...addr, state: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
              <div>
                <Label>PIN code</Label>
                <Input data-testid="checkout-postal" value={addr.postal_code} onChange={(e) => setAddr({ ...addr, postal_code: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
              <div>
                <Label>Country</Label>
                <Input data-testid="checkout-country" value={addr.country} onChange={(e) => setAddr({ ...addr, country: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
              <div className="md:col-span-2">
                <Label>Phone</Label>
                <Input data-testid="checkout-phone" value={addr.phone} onChange={(e) => setAddr({ ...addr, phone: e.target.value })} className="mt-1 bg-white border-[#FFD1A6]" />
              </div>
            </div>
          </div>

          <div className="bg-white/90 border border-[#FFD1A6] rounded-3xl p-8 shadow-warm">
            <div className="font-display text-2xl text-[#2C1810] mb-6">Payment method</div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <button
                data-testid="pay-method-razorpay"
                onClick={() => setMethod("razorpay")}
                className={`text-left p-5 rounded-2xl border-2 transition-colors relative ${method === "razorpay" ? "border-[#E65C00] bg-[#FFF3E0]" : "border-[#FFD1A6] bg-white"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <Wallet className="w-5 h-5 text-[#4A148C]" />
                  <div className="font-body font-semibold text-[#2C1810]">Razorpay</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#4A148C] text-[#FFCF52]">UPI · Cards · NetBanking</span>
                </div>
                <p className="text-xs font-body text-[#5B3D2A]">Best for India — UPI, cards, wallets, netbanking</p>
                <div className="mt-3 font-display text-xl text-[#E65C00] font-semibold">₹{fmtINR(total)}</div>
                <div className="text-[10px] font-body text-[#5B3D2A]/70">≈ ${fmtUSD(total)} USD</div>
                {!razorpayCfg.enabled && (
                  <div data-testid="razorpay-not-configured" className="mt-2 text-[11px] font-body text-[#B71C1C] bg-red-50 border border-red-200 rounded-lg px-2 py-1">
                    Add Razorpay keys in backend/.env to enable
                  </div>
                )}
              </button>

              <button
                data-testid="pay-method-stripe"
                onClick={() => setMethod("stripe")}
                className={`text-left p-5 rounded-2xl border-2 transition-colors ${method === "stripe" ? "border-[#E65C00] bg-[#FFF3E0]" : "border-[#FFD1A6] bg-white"}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <CreditCard className="w-5 h-5 text-[#E65C00]" />
                  <div className="font-body font-semibold text-[#2C1810]">Stripe</div>
                  <span className="text-[10px] px-2 py-0.5 rounded-full bg-[#4A148C] text-[#FFCF52]">International Cards</span>
                </div>
                <p className="text-xs font-body text-[#5B3D2A]">Card checkout hosted by Stripe · charged in ₹</p>
                <div className="mt-3 font-display text-xl text-[#E65C00] font-semibold">₹{fmtINR(total)}</div>
                <div className="text-[10px] font-body text-[#5B3D2A]/70">≈ ${fmtUSD(total)} USD</div>
              </button>
            </div>

            <div className="mt-6 flex items-center gap-2 text-xs font-body text-[#5B3D2A]">
              <ShieldCheck className="w-4 h-4 text-[#E65C00]" />
              Payments are processed securely — we never store your card details.
            </div>
          </div>
        </div>

        <div className="bg-gradient-to-br from-[#4A148C] to-[#2C0855] rounded-3xl p-8 text-[#FFF9F2] h-fit shadow-warm">
          <div className="font-display text-2xl mb-6">Order summary</div>
          <div className="space-y-2 max-h-56 overflow-auto pr-2">
            {items.map((it) => (
              <div key={it.product_id} className="flex justify-between text-sm font-body">
                <span className="truncate mr-2">{it.name} × {it.quantity}</span>
                <span>₹{fmtINR(it.price * it.quantity)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-[#FFCF52]/30 my-4" />
          <div className="space-y-2 font-body text-sm">
            <div className="flex justify-between"><span>Subtotal</span><span>₹{fmtINR(subtotal)}</span></div>
            <div className="flex justify-between"><span>Shipping</span><span>{shipping === 0 ? "Free" : `₹${fmtINR(shipping)}`}</span></div>
            <div className="flex justify-between"><span>GST (5%)</span><span>₹{fmtINR(tax)}</span></div>
          </div>
          <div className="border-t border-[#FFCF52]/30 my-4" />
          <div className="flex justify-between items-baseline mb-1">
            <span className="font-display text-xl">Total</span>
            <span data-testid="checkout-total" className="font-display text-3xl text-[#FFB300] font-semibold">₹{fmtINR(total)}</span>
          </div>
          <div className="text-xs font-body text-[#FFCF52]/70 text-right mb-4">≈ ${fmtUSD(total)} USD</div>
          <Button
            data-testid="checkout-pay-btn"
            onClick={pay}
            disabled={loading}
            className="w-full rounded-full bg-[#E65C00] hover:bg-[#C44A00] text-white h-12 btn-lift"
          >
            {loading ? "Processing…" : (<>Pay with {method === "razorpay" ? "Razorpay" : "Stripe"} <ArrowRight className="w-4 h-4 ml-2" /></>)}
          </Button>
          <p className="text-xs font-body text-[#FFCF52]/70 mt-3 text-center">
            {method === "razorpay" ? "Test card: 4111 1111 1111 1111" : "Test card: 4242 4242 4242 4242"}
          </p>
        </div>
      </div>
    </div>
  );
}
